import { noop } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ConfiguracoesVideochamadaModel } from '../model-videochamada'

interface UseUserMediaOptions {
  video: boolean
  audio: boolean
  videoDeviceId?: string
  audioDeviceId?: string
  onVideoRevoked?: () => void
  onAudioRevoked?: () => void
}

interface GetUserMediaOptions {
  requestAudio: boolean
  requestVideo: boolean
  audioDeviceId?: string
  videoDeviceId?: string
}

export const enableTrackStreamEvent = (track?: MediaStreamTrack | null) =>
  new CustomEvent('enable', { detail: { track } })

const getUserMedia = async ({ requestAudio, requestVideo, audioDeviceId, videoDeviceId }: GetUserMediaOptions) => {
  const audioConstraints = audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true
  const videoConstraints = videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true
  const audio = requestAudio ? audioConstraints : false
  const video = requestVideo ? videoConstraints : false
  return await navigator.mediaDevices?.getUserMedia({ audio, video })
}

// Requisita um único tipo de mídia; cai para sem deviceId se o device exato falhar e retorna
// null se a permissão estiver negada ou não houver device.
const getSingleMedia = async (kind: 'audio' | 'video', deviceId?: string): Promise<MediaStream | null> => {
  const request = { requestAudio: kind === 'audio', requestVideo: kind === 'video' }
  const deviceIds = kind === 'audio' ? { audioDeviceId: deviceId } : { videoDeviceId: deviceId }
  try {
    return (await getUserMedia({ ...request, ...deviceIds })) ?? null
  } catch {
    if (!deviceId) return null
    try {
      return (await getUserMedia(request)) ?? null
    } catch {
      return null
    }
  }
}

// Sinaliza para a UI (que reage ao evento 'enable') que um tipo de mídia ficou indisponível,
// emitindo a track encerrada já desabilitada antes de ela ser removida do stream.
const signalTrackOff = (stream: MediaStream, kind: 'audio' | 'video') => {
  const track = (kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks())[0]
  if (!track) return
  track.enabled = false
  stream.dispatchEvent(enableTrackStreamEvent(track))
}

const isKindLost = (tracks: MediaStreamTrack[]) =>
  tracks.length > 0 && tracks.every((track) => track.readyState !== 'live')

const getInitialStream = async (audioDeviceId?: string, videoDeviceId?: string) => {
  const [audioStream, videoStream] = await Promise.all([
    getSingleMedia('audio', audioDeviceId),
    getSingleMedia('video', videoDeviceId),
  ])
  if (!audioStream && !videoStream) throw new Error('Nenhum dispositivo de mídia disponível')

  const stream = new MediaStream()
  audioStream?.getAudioTracks().forEach((track) => stream.addTrack(track))
  videoStream?.getVideoTracks().forEach((track) => stream.addTrack(track))
  return stream
}

export function useUserMedia(options: UseUserMediaOptions) {
  const { audio, audioDeviceId, video, videoDeviceId, onVideoRevoked, onAudioRevoked } = options

  const [stream, setStream] = useState<MediaStream>()
  const [audioDeviceAvailable, setAudioDeviceAvailable] = useState(false)
  const [videoDeviceAvailable, setVideoDeviceAvailable] = useState(false)
  const [mediaDevices, setMediaDevices] = useState<ConfiguracoesVideochamadaModel>()

  const rawStream = useRef<MediaStream>()
  const lastAudioTrackEnabled = useRef<boolean>()
  const lastVideoTrackEnabled = useRef<boolean>()
  const lastAudioDeviceId = useRef<string>()
  const lastVideoDeviceId = useRef<string>()
  const onVideoRevokedRef = useRef(onVideoRevoked)
  const onAudioRevokedRef = useRef(onAudioRevoked)
  const trackListenersCleanup = useRef<() => void>()
  const recoveringTracks = useRef(false)
  const recoverPending = useRef(false)
  const recoverLostTracksRef = useRef<() => Promise<void>>()
  const mounted = useRef(true)

  useEffect(() => {
    onVideoRevokedRef.current = onVideoRevoked
  }, [onVideoRevoked])

  useEffect(() => {
    onAudioRevokedRef.current = onAudioRevoked
  }, [onAudioRevoked])

  const updateAudioTracks = useCallback((currentStream: MediaStream, audioEnabled: boolean) => {
    currentStream.getAudioTracks().forEach((track) => {
      track.enabled = audioEnabled
      currentStream.dispatchEvent(enableTrackStreamEvent(track))
    })
  }, [])

  const updateVideoTracks = useCallback((currentStream: MediaStream, videoEnabled: boolean) => {
    currentStream.getVideoTracks().forEach((track) => {
      track.enabled = videoEnabled
      currentStream.dispatchEvent(enableTrackStreamEvent(track))
    })
  }, [])

  const updateTracks = useCallback(
    (currentStream: MediaStream, audioEnabled: boolean, videoEnabled: boolean) => {
      const audioChanged = lastAudioTrackEnabled.current !== audioEnabled
      const videoChanged = lastVideoTrackEnabled.current !== videoEnabled

      if (audioChanged) updateAudioTracks(currentStream, audioEnabled)
      if (videoChanged) updateVideoTracks(currentStream, videoEnabled)

      lastAudioTrackEnabled.current = audioEnabled
      lastVideoTrackEnabled.current = videoEnabled
    },
    [updateAudioTracks, updateVideoTracks]
  )

  const updateMediaDevices = useCallback((currentStream: MediaStream) => {
    const audioTrack = currentStream.getAudioTracks()[0]
    const videoTrack = currentStream.getVideoTracks()[0]

    const currentAudioDeviceId = audioTrack?.getSettings()?.deviceId ?? ''
    const audioDeviceLabel = audioTrack?.label ?? ''

    const currentVideoDeviceId = videoTrack?.getSettings()?.deviceId ?? ''
    const videoDeviceLabel = videoTrack?.label ?? ''

    setMediaDevices({
      audioInput: { id: currentAudioDeviceId, nome: audioDeviceLabel },
      audioOutput: { id: 'default', nome: 'Padrão' },
      videoInput: { id: currentVideoDeviceId, nome: videoDeviceLabel },
    })
  }, [])

  const attachTrackHealthListeners = useCallback((currentStream: MediaStream) => {
    trackListenersCleanup.current?.()

    const videoTrack = currentStream.getVideoTracks()[0]
    const audioTrack = currentStream.getAudioTracks()[0]

    const handleTrackLost = () => {
      recoverLostTracksRef.current?.().catch(noop)
    }

    // Só 'ended' (perda definitiva); 'mute' é ignorado por ser transitório.
    videoTrack?.addEventListener('ended', handleTrackLost)
    audioTrack?.addEventListener('ended', handleTrackLost)

    trackListenersCleanup.current = () => {
      videoTrack?.removeEventListener('ended', handleTrackLost)
      audioTrack?.removeEventListener('ended', handleTrackLost)
    }
  }, [])

  const replaceTrack = useCallback(
    (
      currentStream: MediaStream,
      kind: 'audio' | 'video',
      nextTrack: MediaStreamTrack | undefined,
      enabled: boolean
    ) => {
      const currentTracks = kind === 'audio' ? currentStream.getAudioTracks() : currentStream.getVideoTracks()

      currentTracks.forEach((track) => {
        currentStream.removeTrack(track)
        track.stop()
      })

      if (nextTrack) {
        nextTrack.enabled = enabled
        currentStream.addTrack(nextTrack)
        currentStream.dispatchEvent(enableTrackStreamEvent(nextTrack))
      }
    },
    []
  )

  const applyRecoveredTrack = useCallback(
    (
      current: MediaStream,
      kind: 'audio' | 'video',
      lostTrack: MediaStreamTrack,
      recovered: MediaStream | null,
      lastEnabled: boolean | undefined,
      setAvailable: (available: boolean) => void,
      onRevoked: () => void
    ) => {
      const currentTrack = (kind === 'audio' ? current.getAudioTracks() : current.getVideoTracks())[0]
      if (currentTrack !== lostTrack) {
        // syncStream (troca de device) substituiu a track durante o await: não sobrescreve a seleção do usuário
        recovered?.getTracks().forEach((t) => t.stop())
        return
      }

      const track = (kind === 'audio' ? recovered?.getAudioTracks() : recovered?.getVideoTracks())?.[0]
      if (!track) signalTrackOff(current, kind)
      replaceTrack(current, kind, track, lastEnabled ?? true)
      setAvailable(!!track)
      if (!track) onRevoked()
    },
    [replaceTrack]
  )

  // Ao revogar uma permissão em runtime o navegador encerra todas as tracks da aba, não só a do
  // dispositivo negado. Reaquire o que foi encerrado: recupera o dispositivo ainda permitido e marca
  // como indisponível apenas o que de fato foi negado.
  const recoverLostTracks = useCallback(async () => {
    const current = rawStream.current
    if (!current) return
    if (recoveringTracks.current) {
      recoverPending.current = true
      return
    }
    recoveringTracks.current = true

    try {
      const audioTracks = current.getAudioTracks()
      const videoTracks = current.getVideoTracks()
      const audioLost = isKindLost(audioTracks)
      const videoLost = isKindLost(videoTracks)
      if (!audioLost && !videoLost) return

      const lostAudioTrack = audioTracks[0]
      const lostVideoTrack = videoTracks[0]

      const [recoveredAudio, recoveredVideo] = await Promise.all([
        audioLost ? getSingleMedia('audio', lastAudioDeviceId.current) : Promise.resolve<MediaStream | null>(null),
        videoLost ? getSingleMedia('video', lastVideoDeviceId.current) : Promise.resolve<MediaStream | null>(null),
      ])

      if (!mounted.current || rawStream.current !== current) {
        recoveredAudio?.getTracks().forEach((t) => t.stop())
        recoveredVideo?.getTracks().forEach((t) => t.stop())
        return
      }

      trackListenersCleanup.current?.()

      if (audioLost) {
        applyRecoveredTrack(
          current,
          'audio',
          lostAudioTrack,
          recoveredAudio,
          lastAudioTrackEnabled.current,
          setAudioDeviceAvailable,
          () => onAudioRevokedRef.current?.()
        )
      }
      if (videoLost) {
        applyRecoveredTrack(
          current,
          'video',
          lostVideoTrack,
          recoveredVideo,
          lastVideoTrackEnabled.current,
          setVideoDeviceAvailable,
          () => onVideoRevokedRef.current?.()
        )
      }

      updateMediaDevices(current)
      attachTrackHealthListeners(current)
    } finally {
      recoveringTracks.current = false
      if (recoverPending.current) {
        recoverPending.current = false
        recoverLostTracksRef.current?.().catch(noop)
      }
    }
  }, [applyRecoveredTrack, attachTrackHealthListeners, updateMediaDevices])

  useEffect(() => {
    recoverLostTracksRef.current = recoverLostTracks
  }, [recoverLostTracks])

  const acquireInitialStream = useCallback(
    async ({ audio, audioDeviceId, video, videoDeviceId }: UseUserMediaOptions, isCurrent: () => boolean) => {
      const initialStream = await getInitialStream(audioDeviceId, videoDeviceId)

      if (!isCurrent()) {
        initialStream?.getTracks().forEach((t) => t.stop())
        return
      }

      if (!initialStream) return

      rawStream.current = initialStream
      setStream(initialStream)

      setAudioDeviceAvailable(!!initialStream.getAudioTracks().length)
      setVideoDeviceAvailable(!!initialStream.getVideoTracks().length)

      updateTracks(initialStream, audio, video)
      updateMediaDevices(initialStream)
      attachTrackHealthListeners(initialStream)

      lastAudioDeviceId.current = audioDeviceId
      lastVideoDeviceId.current = videoDeviceId
    },
    [attachTrackHealthListeners, updateMediaDevices, updateTracks]
  )

  const syncStream = useCallback(
    async (options: UseUserMediaOptions, isCurrent: () => boolean) => {
      const { audio, audioDeviceId, video, videoDeviceId } = options
      try {
        if (!rawStream.current) {
          await acquireInitialStream(options, isCurrent)
          return
        }

        const currentStream = rawStream.current
        const audioDeviceChanged = lastAudioDeviceId.current !== audioDeviceId
        const videoDeviceChanged = lastVideoDeviceId.current !== videoDeviceId

        if (audioDeviceChanged || videoDeviceChanged) {
          const replacementStream = await getUserMedia({
            requestAudio: audioDeviceChanged,
            requestVideo: videoDeviceChanged,
            audioDeviceId,
            videoDeviceId,
          })

          if (!isCurrent()) {
            replacementStream?.getTracks().forEach((t) => t.stop())
            return
          }

          // Remove listeners antes de parar as tracks antigas para evitar falso onVideoRevoked
          trackListenersCleanup.current?.()

          if (audioDeviceChanged) {
            replaceTrack(currentStream, 'audio', replacementStream?.getAudioTracks()[0], audio)
            lastAudioDeviceId.current = audioDeviceId
          }

          if (videoDeviceChanged) {
            replaceTrack(currentStream, 'video', replacementStream?.getVideoTracks()[0], video)
            lastVideoDeviceId.current = videoDeviceId
          }

          setAudioDeviceAvailable(!!currentStream.getAudioTracks().length)
          setVideoDeviceAvailable(!!currentStream.getVideoTracks().length)
          updateMediaDevices(currentStream)
          attachTrackHealthListeners(currentStream)
        }

        updateTracks(currentStream, audio, video)
      } catch {
        if (rawStream.current) {
          setAudioDeviceAvailable(!!rawStream.current.getAudioTracks().length)
          setVideoDeviceAvailable(!!rawStream.current.getVideoTracks().length)
          updateMediaDevices(rawStream.current)
        } else {
          setVideoDeviceAvailable(false)
          setAudioDeviceAvailable(false)
        }
      }
    },
    [acquireInitialStream, attachTrackHealthListeners, replaceTrack, updateMediaDevices, updateTracks]
  )

  useEffect(() => {
    let canceled = false
    const isCurrent = () => !canceled

    syncStream({ audio, audioDeviceId, video, videoDeviceId }, isCurrent)

    const handleDeviceChange = () => syncStream({ audio, audioDeviceId, video, videoDeviceId }, isCurrent)
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)

    return () => {
      canceled = true
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [syncStream, audio, audioDeviceId, video, videoDeviceId])

  // Para de usar os dispositivos de mídia ao destruir o componente
  useEffect(() => {
    return () => {
      mounted.current = false
      trackListenersCleanup.current?.()
      rawStream.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  return { stream, audioDeviceAvailable, videoDeviceAvailable, mediaDevices }
}
